import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { DELIVERY_INTENT_EVENT } from './utils/deliveryCallIntent';
import Login from './components/Login';
import Landing from './components/landing/Landing';
import Navigation from './components/Navigation';
import OrdersPage from './components/OrdersPage';
import Contact from './components/Contact';
import About from './components/About';
import SuperAdminPanel from './components/superadmin/SuperAdminPanel';
import SupportPanel from './components/support/SupportPanel';
import SalesPanel from './components/sales/SalesPanel';
import StoreAdminDashboard from './components/StoreAdminDashboard';
import OrgDashboard from './components/OrgDashboard';
import TakeAway from './components/TakeAway';
import Delivery from './components/DeliveryWithMap';
import SelfCheckoutKiosk from './components/SelfCheckoutKiosk';
import { DineInOperations } from './components/dineIn';
import WaiterApp from './components/waiter/app/WaiterApp';
import KitchenDisplay from './components/kitchen/KitchenDisplay';
import IncomingCallPopup from './components/IncomingCallPopup';
import GlobalPosPreloadToast from './components/GlobalPosPreloadToast';
import GlobalPrintHealth from './components/GlobalPrintHealth';
import StaleRegisterBanner from './components/StaleRegisterBanner';
import IncomingOrderQueue from './components/IncomingOrderQueue';
import PlatformCancelAlert from './components/PlatformCancelAlert';
import { ToastProvider } from './components/shared/Toast';
import { organizationAPI } from './services/api';
import { applyStoreTheme, clearStoreTheme, restoreCachedStoreTheme } from './utils/storeTheme';
import './App.css';
import './components/WorkspaceLight.css';
import './components/CashierLight.css';
import './components/CashierV2.css';
import './components/CashierV2Dark.css';
import './components/RoleLight.css';
import './components/RoleV2.css';
import './components/BrandNet.css';

// Εμφάνιση καταστήματος (migration 263): το θέμα της τελευταίας σύνδεσης αυτής της συσκευής ΠΡΙΝ από
// το πρώτο render — αλλιώς σε κάθε F5 το ταμείο θα έδειχνε μπλε ώσπου να απαντήσει το /auth/me, και το
// ThemeContext δεν θα ήξερε την προεπιλογή ανοιχτό/σκούρο του καταστήματος. Το /me τη διορθώνει μετά.
restoreCachedStoreTheme();

function App() {
  const { isAuthenticated, loading, employee, store, storeSession, employeeLogout } = useAuth();
  const { t } = useTranslation();

  // Το θέμα που ισχύει: του καταστήματος της σύνδεσης, ή (στην οθόνη επιλογής υπαλλήλου/PIN) της
  // συνεδρίας καταστήματος. Χωρίς κατάστημα (αποσύνδεση, χρήστης οργανισμού στα κεντρικά) → η
  // εμφάνιση της εφαρμογής. Όσο φορτώνει, μένει το αποθηκευμένο (κανένα αναβόσβημα).
  const activeStoreTheme = store?.theme ?? (!isAuthenticated ? storeSession?.store?.theme : null) ?? null;
  const activeStoreThemeKey = JSON.stringify(activeStoreTheme);
  useEffect(() => {
    if (loading) return;
    if (activeStoreTheme) applyStoreTheme(activeStoreTheme);
    else clearStoreTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- το κλειδί είναι το περιεχόμενο του θέματος
  }, [loading, activeStoreThemeKey]);
  
  // Set initial page based on current URL path
  const getInitialPage = () => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path === '/orders') return 'orders';
      if (path === '/settings') return 'settings';
      if (path === '/takeaway') return 'takeaway';
      if (path === '/delivery') return 'delivery';
      if (path === '/dinein') return 'dinein';
    }
    return 'orders'; // Default to orders for cashiers (no dashboard access)
  };
  
  const [currentPage, setCurrentPage] = useState<'dashboard' | 'orders' | 'settings' | 'takeaway' | 'delivery' | 'dinein'>(getInitialPage() as 'dashboard' | 'orders' | 'settings' | 'takeaway' | 'delivery' | 'dinein');

  const handlePageChange = (page: string) => {
    // Update both state AND URL to ensure routing works correctly
    // Without updating the URL, pathname-based checks in the render logic
    // would keep rendering the old component
    const newPath = page === 'dashboard' ? '/' : `/${page}`;
    if (window.location.pathname !== newPath) {
      window.history.pushState({}, '', newPath);
    }
    setCurrentPage(page as 'dashboard' | 'orders' | 'settings' | 'takeaway' | 'delivery' | 'dinein');
  };

  // Αναγγελία κλήσης («Μετάβαση» / «Ξανά τα ίδια» / «Νέα παραγγελία»): στη σελίδα delivery ΧΩΡΙΣ
  // φόρτωση σελίδας (ως 12/10/2026 window.location.href). Την πρόθεση (τηλέφωνο, παραγγελία)
  // την παίρνει το ίδιο το DeliveryWithMap — utils/deliveryCallIntent.
  const [, setNavTick] = useState(0);
  useEffect(() => {
    const toDelivery = () => {
      if (window.location.pathname !== '/delivery') window.history.pushState({}, '', '/delivery');
      setCurrentPage('delivery');
      // Νέα ζωγραφιά και όταν το currentPage ήταν ήδη 'delivery' (οι κλάδοι διαβάζουν το pathname).
      setNavTick(n => n + 1);
    };
    window.addEventListener(DELIVERY_INTENT_EVENT, toDelivery);
    return () => window.removeEventListener(DELIVERY_INTENT_EVENT, toDelivery);
  }, []);

  // Ο διανομέας δεν συνδέεται πια (απόφαση ιδιοκτήτη 12/10/2026, routes/auth.js ROLE_CANNOT_LOGIN): ένα
  // token του από πριν γυρίζει στην επιλογή υπαλλήλου — ποτέ στο ταμείο.
  const isDriverSession = isAuthenticated && employee?.role === 'driver';
  useEffect(() => {
    if (isDriverSession) employeeLogout();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- μία φορά ανά συνεδρία διανομέα
  }, [isDriverSession]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto"></div>
          <p className="mt-4 text-t2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // This handles employee logout from any page — no need to be on /login path
    if (storeSession) {
      return <Login />;
    }
    
    // Lightweight routes without React Router
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path === '/contact') return <Contact />;
      if (path === '/about') return <About />;
    }
    // Go straight to Login (Landing page disabled)
    return <Login />;
  }

  // Check user role and redirect accordingly
  
  const isSuperAdmin = employee?.role === 'super_admin';
  const isSupport = employee?.role === 'support';
  const isSales = employee?.role === 'sales';
  const isStoreAdmin = employee?.role === 'store_admin';
  const isOrgAdmin = employee?.role === 'org_admin';
  const isKiosk = employee?.role === 'kiosk';
  const isWaiter = employee?.role === 'waiter' || employee?.role === 'senior_waiter';
  const isKitchen = employee?.role === 'kitchen';
  
  const showCallPopup = !isKiosk && !isKitchen && !isWaiter;

  // Handle kiosk role - show dedicated kiosk interface
  if (isKiosk) {
    return <div className="role-workspace role-kiosk"><SelfCheckoutKiosk /></div>;
  }
  
  // Handle kitchen role - show kitchen display
  if (isKitchen) {
    return <div className="role-workspace role-kitchen"><KitchenDisplay /></div>;
  }
  
  // Handle waiter roles — αποκλειστικό app κινητού (WaiterApp). Ξεχωριστό UI
  // από την κονσόλα ταμία· κοινά μοιράζονται μόνο services/dineInApi και
  // utils/dineInMoney, ώστε ποσά και endpoints να μην αποκλίνουν ποτέ.
  // Η παλιά οθόνη σερβιτόρου (το «δίχτυ ασφαλείας» σε ξεχωριστή διαδρομή)
  // αποσύρθηκε στη Φ1 του base_price (απόφαση 16): χρέωνε base_price +
  // price_modifier χωρίς την τιμή dine-in, άρα θα απέκλινε από το ταμείο μόλις
  // τα προϊόντα περάσουν σε «τιμή μόνο στο μέγεθος». Κάθε διαδρομή, και η παλιά
  // διεύθυνση από σελιδοδείκτη, καταλήγει πλέον στο WaiterApp.
  // Χάνονται για τον σερβιτόρο (υπήρχαν ΜΟΝΟ στην παλιά οθόνη): κέρασμα, έκπτωση,
  // μεταφορά τραπεζιού/είδους, ακύρωση, διόρθωση, τιμολόγιο — γίνονται πλέον μόνο
  // από την κονσόλα ταμία (DineInOperations). Προέλεγχος: docs/base_price_phase1_plan.md §8.1.
  if (isWaiter) {
    return <div className="role-workspace role-waiter"><WaiterApp /></div>;
  }
  
  // Η εφαρμογή οδηγού καταργήθηκε (13/10/2026)· παλιό token διανομέα: τίποτα ώσπου να αποσυνδεθεί (πιο πάνω).
  if (isDriverSession) return null;
  
  // Handle org_admin role - show organization dashboard with store switcher
  if (isOrgAdmin) {
    return <div className="role-workspace role-admin"><OrgDashboard /></div>;
  }
  
  // Handle super admin routes
  if (isSuperAdmin) {
    return <div className="role-workspace role-admin"><SuperAdminPanel /></div>;
  }

  // Handle support role - HQ helpdesk panel
  if (isSupport) {
    return <div className="role-workspace role-admin"><SupportPanel /></div>;
  }

  // Ρόλος 'sales' (migration 285): μόνο οι Πωλήσεις
  if (isSales) {
    return <div className="role-workspace role-admin"><SalesPanel /></div>;
  }
  
  // Handle store admin routes
  if (isStoreAdmin) {
    if (typeof window !== 'undefined') {
      // «Προϊόντα» (/products, σελιδοδείκτες): είναι πια η καρτέλα «Κατάλογος» της διαχείρισης.
      if (window.location.pathname === '/products') {
        localStorage.setItem('storeAdminActiveTab', 'catalog');
        window.history.replaceState({}, '', '/store-admin');
      }

      // Check for takeaway route (for cashiers) - only if takeaway is enabled
      if (window.location.pathname === '/takeaway' && (employee?.role === 'cashier' || employee?.role === 'store_admin')) {
        if (store?.takeaway === false) {
          window.location.pathname = '/orders';
          return null;
        }
        return <div className="cashier-workspace"><IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue /><TakeAway onPageChange={handlePageChange} /></div>;
      }
      // Check for delivery route (for cashiers)
      if (window.location.pathname === '/delivery' && (employee?.role === 'cashier' || employee?.role === 'store_admin')) {
        return <div className="cashier-workspace"><IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue /><Delivery onPageChange={handlePageChange} /></div>;
      }
      // Check for kitchen display route
      if (window.location.pathname === '/kitchen') {
        return <div className="role-workspace role-kitchen"><KitchenDisplay /></div>;
      }
      // Dine-in POS — πριν έλειπε από το whitelist και ο store_admin
      // γύριζε σιωπηλά στο dashboard χωρίς να μπορεί να ανοίξει ΠΟΤΕ τα τραπέζια.
      if (window.location.pathname === '/dinein' && store?.dine_in === true) {
        return (
          <div className="cashier-workspace min-h-screen bg-surface-1">
            <IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue />
            <Navigation currentPage="dinein" onPageChange={handlePageChange} />
            <DineInOperations />
          </div>
        );
      }
      return <div className="role-workspace role-admin"><IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue /><StoreAdminDashboard /></div>;
    }
  }
  
  // Check for takeaway route for regular cashiers - only if takeaway is enabled
  if (typeof window !== 'undefined' && window.location.pathname === '/takeaway') {
    if (store?.takeaway === false) {
      window.location.pathname = '/orders';
      return null;
    }
    return <div className="cashier-workspace"><IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue /><TakeAway onPageChange={handlePageChange} /></div>;
  }

  // Check for delivery route for regular cashiers
  if (typeof window !== 'undefined' && window.location.pathname === '/delivery') {
    return <div className="cashier-workspace"><IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue /><Delivery onPageChange={handlePageChange} /></div>;
  }

  // TakeAway and Delivery have their own Navigation, render them directly
  if (currentPage === 'takeaway') {
    if (store?.takeaway === false) {
      setCurrentPage('orders');
      return null;
    }
    return <div className="cashier-workspace"><IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue /><TakeAway onPageChange={handlePageChange} /></div>;
  }
  
  if (currentPage === 'delivery') {
    return <div className="cashier-workspace"><IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue /><Delivery onPageChange={handlePageChange} /></div>;
  }

  // DineIn has its own layout with Navigation
  if (currentPage === 'dinein') {
    return (
      <div className="cashier-workspace min-h-screen bg-surface-1">
        <IncomingCallPopup /><GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue />
        <Navigation 
          currentPage="dinein"
          onPageChange={handlePageChange} 
        />
        <DineInOperations />
      </div>
    );
  }

  // Το παλιό ταμπλό του ταμία (DashboardPage) καταργήθηκε 13/10/2026: κάθε άλλη σελίδα → Παραγγελίες.
  const renderCurrentPage = () => <OrdersPage />;

  return (
    <div className="cashier-workspace min-h-screen bg-surface-1">
      {showCallPopup && <IncomingCallPopup />}
      <GlobalPosPreloadToast /><GlobalPrintHealth /><StaleRegisterBanner /><IncomingOrderQueue />
      <Navigation 
        currentPage={currentPage as 'dashboard' | 'orders' | 'takeaway' | 'delivery' | 'dinein'} 
        onPageChange={handlePageChange} 
      />
      <main>
        {renderCurrentPage()}
      </main>
    </div>
  );
}

// Floating "back to organization" control, shown when an org user has switched into a store.
// Rendered as a sibling of <App/> so it persists across every store sub-view.
const OrgStoreReturnBanner: React.FC = () => {
  const { employee } = useAuth();
  const ctx = (employee as any)?.org_context;
  const storeName = (employee as any)?.active_store_name;
  const [busy, setBusy] = useState(false);
  if (!ctx) return null;
  const exit = async () => {
    setBusy(true);
    try {
      const data = await organizationAPI.exitStore();
      localStorage.setItem('xentrix_token', data.token);
      window.location.href = '/';
    } catch {
      setBusy(false);
      alert('Αποτυχία επιστροφής στον όμιλο');
    }
  };
  return (
    <div style={{ position: 'fixed', bottom: 12, right: 12, zIndex: 9999 }}>
      <button
        onClick={exit}
        disabled={busy}
        title="Επιστροφή στο dashboard του ομίλου"
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 10, background: '#d97706', color: '#fff', fontSize: 13, fontWeight: 600, boxShadow: '0 6px 20px rgba(0,0,0,0.35)', border: 'none', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}
      >
        ← Επιστροφή στον όμιλο{storeName ? ` · ${storeName}` : ''}
      </button>
    </div>
  );
};

// Wrap App with ToastProvider
const AppWithProviders = () => (
  <ToastProvider>
    <OrgStoreReturnBanner />
    {/* ΜΙΑ φορά για όλες τις οθόνες (και κουζίνα) — βλ. PlatformCancelAlert */}
    <PlatformCancelAlert />
    <App />
  </ToastProvider>
);

export default AppWithProviders;
